import { skipToken, useQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

/**
 * Kept out of the shared account query on purpose: `teamMemberCounts` is
 * non-nullable and needs `viewTeamMembers`, so a member without it would
 * fail the whole operation. Only query it for Roles organizations; legacy
 * organizations get empty counts.
 */
export const GetTeamMemberCounts = graphql(/* GraphQL */ `
  query GetTeamMemberCounts {
    account {
      currentOrganization {
        teamMemberCounts {
          total
          limit
          pendingInvitations
          byRole {
            role
            count
            limit
          }
        }
      }
    }
  }
`)

export type TeamMemberCounts = NonNullable<
  ResultOf<typeof GetTeamMemberCounts>['account']['currentOrganization']
>['teamMemberCounts']

/**
 * Organization-wide counts of active team members, by role, for the Roles
 * Team Members summary cards. Null while loading, on error, or when
 * `enabled` is false (the query is skipped).
 */
export const useTeamMemberCounts = ({
  enabled,
}: {
  enabled: boolean
}): TeamMemberCounts | null => {
  // skipToken rather than `skip`: refetchQueries passes over a query that
  // has only ever been skipped this way, so a mutation's refetch cannot issue
  // the counts query for a member who never had viewTeamMembers.
  const { data } = useQuery(
    GetTeamMemberCounts,
    enabled ? { fetchPolicy: 'cache-and-network' } : skipToken,
  )

  // A skipped query keeps its last result, so gate the data on `enabled` too.
  if (!enabled) return null
  return data?.account?.currentOrganization?.teamMemberCounts ?? null
}
